/**
 * Platform detection + bridge readiness for Capacitor native builds.
 *
 * Two guarantees this module provides:
 *
 * 1. `isNativePlatform()` is STICKY. Once we've observed `window.Capacitor`
 *    once, we treat this app instance as native forever. This defends
 *    against any hypothetical later call where `window.Capacitor` briefly
 *    appears missing (defensive — we've never seen this, but the auth
 *    storage path is too important to leave to a per-call sync check).
 *
 * 2. `platformReady` resolves AFTER `main.tsx` has (a) given the Capacitor
 *    bridge a brief chance to inject `window.Capacitor`, and (b) run
 *    `initNative()` on native. Every storage adapter call awaits this
 *    promise before deciding native-vs-web, so a supabase-js write that
 *    fires immediately on cold boot cannot race the bridge and end up
 *    routed to WebView storage (which Android force-close wipes).
 */

let nativeSticky = false;

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  platform?: string;
};

export function isNativePlatform(): boolean {
  if (nativeSticky) return true;
  if (typeof window === "undefined") return false;
  const cap = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
  if (!cap) return false;
  const isNative = typeof cap.isNativePlatform === "function"
    ? cap.isNativePlatform()
    : cap.platform === "ios" || cap.platform === "android";
  if (isNative) nativeSticky = true;
  return isNative;
}

// Resolved by `main.tsx` after platform decision + initNative complete.
// Storage adapter awaits this before branching native-vs-web.
let _resolvePlatformReady: () => void = () => {};
export const platformReady: Promise<void> = new Promise<void>((resolve) => {
  _resolvePlatformReady = resolve;
});
export function markPlatformReady(): void {
  _resolvePlatformReady();
}

/**
 * Give the Capacitor bridge a brief window to inject `window.Capacitor`
 * before we branch on platform. In practice the bridge is set before app
 * JS evaluates, but on cold-boot / slow devices we've seen the first
 * synchronous check race. 400ms is invisible to the user and eliminates
 * the class of bug where main.tsx wrongly takes the web branch on native.
 * On web, this resolves immediately once the timeout passes with no
 * Capacitor global observed (or sooner if window is undefined).
 */
export async function waitForCapacitorBridge(timeoutMs = 400): Promise<void> {
  if (typeof window === "undefined") return;
  if ((window as unknown as { Capacitor?: unknown }).Capacitor) return;
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, 20));
    if ((window as unknown as { Capacitor?: unknown }).Capacitor) return;
  }
}
